import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    Breadcrumbs,
    Container
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
    TrendingUp,
    TrendingDown,
    People,
    PersonAdd,
    CheckCircle,
    Warning
} from '@mui/icons-material';
import { reportAPI } from '../../services/reportAPI';
import { ReportChart } from './ReportChart';

interface ManagementReportData {
    members: {
        totalMembers: number;
        activeMembers: number;
        activeMembersLastMonth: number;
        membersByTier: Array<{ tierName: string; count: number; percentage: number }>;
        membersByStatus: Array<{ status: string; count: number; percentage: number }>;
        newMembersThisMonth: number;
        newMembersLastMonth: number;
        churnRate: number;
    };
    onboarding: {
        totalMembers: number;
        completed: number;
        incomplete: number;
        completionRate: number;
    };
}

export const ManagementReport: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<ManagementReportData | null>(null);

    useEffect(() => {
        loadReportData();
        // Log report access
        reportAPI.logReportAccess('management-report');
    }, []);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await reportAPI.getDashboardOverview();

            // The API now returns the data in the correct format
            const reportData: ManagementReportData = {
                members: data.members,
                onboarding: data.onboarding
            };

            setReportData(reportData);
        } catch (err) {
            setError('Failed to load management report data');
            console.error('Error loading management report:', err);
        } finally {
            setLoading(false);
        }
    };

    const calculateMoMChange = () => {
        if (!reportData) return { change: 0, percentage: 0, isPositive: true };

        const currentMonth = reportData.members.activeMembers;
        const lastMonth = reportData.members.activeMembersLastMonth || 0;
        const change = currentMonth - lastMonth;
        const percentage = lastMonth > 0 ? ((change / lastMonth) * 100) : 0;

        return {
            change,
            percentage,
            isPositive: change >= 0
        };
    };

    const formatPercentage = (value: number): string => {
        return `${value.toFixed(1)}%`;
    };

    const handleTierClick = (tierName: string) => {
        // Navigate to member management with tier filter
        navigate('/member-management', { 
            state: { 
                filterTier: tierName 
            } 
        });
    };

    const handleStatusClick = (status: string) => {
        // Navigate to member management with status filter
        navigate('/member-management', { 
            state: { 
                filterStatus: status 
            } 
        });
    };

    const handleOnboardingClick = (isComplete: boolean) => {
        // Navigate to member management with onboarding filter
        navigate('/member-management', { 
            state: { 
                filterOnboardingComplete: isComplete ? 'complete' : 'incomplete'
            } 
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!reportData) {
        return null;
    }

    const momChange = calculateMoMChange();

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Management Report</Typography>
            </Breadcrumbs>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Management Report</Typography>
                <Typography variant="body2" color="textSecondary">
                    Last updated: {new Date().toLocaleString()}
                </Typography>
            </Box>

            {/* Key Metrics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Active Members */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <People sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography color="textSecondary" variant="body2">
                                    Active Members
                                </Typography>
                            </Box>
                            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                                {reportData.members.activeMembers}
                            </Typography>
                            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                Active as of {new Date().toLocaleDateString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* New Members This Month */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <PersonAdd sx={{ mr: 1, color: 'success.main' }} />
                                <Typography color="textSecondary" variant="body2">
                                    New Members This Month
                                </Typography>
                            </Box>
                            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                                {reportData.members.newMembersThisMonth}
                            </Typography>
                            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                New signups
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Month over Month Change */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                {momChange.isPositive ? (
                                    <TrendingUp sx={{ mr: 1, color: 'success.main' }} />
                                ) : (
                                    <TrendingDown sx={{ mr: 1, color: 'error.main' }} />
                                )}
                                <Typography color="textSecondary" variant="body2">
                                    Month over Month
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="baseline" gap={1}>
                                <Typography
                                    variant="h3"
                                    component="div"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: momChange.isPositive ? 'success.main' : 'error.main'
                                    }}
                                >
                                    {momChange.isPositive ? '+' : ''}{momChange.change}
                                </Typography>
                                <Chip
                                    label={`${momChange.isPositive ? '+' : ''}${formatPercentage(momChange.percentage)}`}
                                    size="small"
                                    color={momChange.isPositive ? 'success' : 'error'}
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>
                            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                vs. last month ({reportData.members.activeMembersLastMonth || 0} active)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Onboarding Status */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <CheckCircle sx={{ mr: 1, color: 'info.main' }} />
                                <Typography color="textSecondary" variant="body2">
                                    Onboarding Complete
                                </Typography>
                            </Box>
                            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                                {reportData.onboarding.completed}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                <Typography color="textSecondary" variant="body2">
                                    {formatPercentage(reportData.onboarding.completionRate)} completion rate
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Onboarding Incomplete */}
                <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                    <Card elevation={2}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Warning sx={{ mr: 1, color: 'warning.main' }} />
                                <Typography color="textSecondary" variant="body2">
                                    Onboarding Incomplete
                                </Typography>
                            </Box>
                            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                                {reportData.onboarding.incomplete}
                            </Typography>
                            <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                                Require attention
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3}>
                {/* Members by Tier */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Tiers
                        </Typography>
                        {reportData.members.membersByTier.length > 0 ? (
                            <>
                                <ReportChart
                                    type="pie"
                                    data={{
                                        labels: reportData.members.membersByTier.map(item => 
                                            `${item.tierName}: ${item.count} (${formatPercentage(item.percentage)})`
                                        ),
                                        datasets: [{
                                            data: reportData.members.membersByTier.map(item => item.count),
                                            backgroundColor: [
                                                '#FF6384',
                                                '#36A2EB',
                                                '#FFCE56',
                                                '#4BC0C0',
                                                '#9966FF',
                                                '#FF9F40'
                                            ]
                                        }]
                                    }}
                                    height={250}
                                    options={{
                                        onHover: (event: any, activeElements: any) => {
                                            const canvas = event.native?.target;
                                            if (canvas) {
                                                canvas.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
                                            }
                                        },
                                        onClick: (event: any, elements: any) => {
                                            if (elements && elements.length > 0) {
                                                const index = elements[0].index;
                                                const tierName = reportData.members.membersByTier[index].tierName;
                                                handleTierClick(tierName);
                                            }
                                        },
                                        plugins: {
                                            legend: {
                                                position: 'left'
                                            },
                                            tooltip: {
                                                callbacks: {
                                                    label: function(context: any) {
                                                        const item = reportData.members.membersByTier[context.dataIndex];
                                                        return `${item.tierName}: ${item.count} members (${formatPercentage(item.percentage)})`;
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </>
                        ) : (
                            <Typography color="textSecondary" variant="body2">
                                No tier data available
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Member Status Distribution */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Status
                        </Typography>
                        {reportData.members.membersByStatus.length > 0 ? (
                            <>
                                <ReportChart
                                    type="doughnut"
                                    data={{
                                        labels: reportData.members.membersByStatus.map(item =>
                                            `${item.status.charAt(0).toUpperCase() + item.status.slice(1)}: ${item.count} (${formatPercentage(item.percentage)})`
                                        ),
                                        datasets: [{
                                            data: reportData.members.membersByStatus.map(item => item.count),
                                            backgroundColor: [
                                                '#FF9800',
                                                '#4CAF50',
                                                '#F44336',
                                                '#9E9E9E',
                                                '#2196F3',
                                                '#9C27B0'
                                            ]
                                        }]
                                    }}
                                    height={250}
                                    options={{
                                        onHover: (event: any, activeElements: any) => {
                                            const canvas = event.native?.target;
                                            if (canvas) {
                                                canvas.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
                                            }
                                        },
                                        onClick: (event: any, elements: any) => {
                                            if (elements && elements.length > 0) {
                                                const index = elements[0].index;
                                                const status = reportData.members.membersByStatus[index].status;
                                                handleStatusClick(status);
                                            }
                                        },
                                        plugins: {
                                            legend: {
                                                position: 'right'
                                            },
                                            tooltip: {
                                                callbacks: {
                                                    label: function(context: any) {
                                                        const item = reportData.members.membersByStatus[context.dataIndex];
                                                        return `${item.status}: ${item.count} members (${formatPercentage(item.percentage)})`;
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </>
                        ) : (
                            <Typography color="textSecondary" variant="body2">
                                No status data available
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Onboarding Status Breakdown */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Onboarding 
                        </Typography>
                        <ReportChart
                            type="bar"
                            data={{
                                labels: ['Complete', 'Incomplete'],
                                datasets: [{
                                    label: 'Status',
                                    data: [
                                        reportData.onboarding.completed,
                                        reportData.onboarding.incomplete
                                    ],
                                    backgroundColor: [
                                        '#4CAF50',
                                        '#FF9800'
                                    ]
                                }]
                            }}
                            height={250}
                            options={{
                                onHover: (event: any, activeElements: any) => {
                                    const canvas = event.native?.target;
                                    if (canvas) {
                                        canvas.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
                                    }
                                },
                                onClick: (event: any, elements: any) => {
                                    if (elements && elements.length > 0) {
                                        const index = elements[0].index;
                                        const isComplete = index === 0; // 0 = Complete, 1 = Incomplete
                                        handleOnboardingClick(isComplete);
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            stepSize: 1
                                        }
                                    }
                                },
                                plugins: {
                                    tooltip: {
                                        callbacks: {
                                            label: function(context: any) {
                                                const value = context.parsed.y;
                                                const total = reportData.onboarding.totalMembers;
                                                const percentage = total > 0 ? (value / total) * 100 : 0;
                                                return `${context.dataset.label}: ${value} (${formatPercentage(percentage)})`;
                                            }
                                        }
                                    }
                                }
                            }}
                        />
                      
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};
